import { guildTemplateError, importGuildTemplates } from "./api";
import { ImportCsvModal } from "./ImportCsvModal";
import { cardSummary, type CardKey } from "./roster";
import type {
  GuildTemplateImportResult,
  GuildTemplateImportRow,
} from "./types";
import styles from "./GuildTemplatesTab.module.css";

/**
 * Import a table of guild templates from a CSV — ImportCsvModal, told which
 * table it is looking at.
 *
 * The server answers in the roster import's own vocabulary — the same groups,
 * the same refused lines, the same counts — and that is why this file is
 * thirty lines rather than four hundred: what a templates import is and a
 * roster import is not comes to the noun, the endpoint, the header and three
 * preview columns.
 *
 * Mockup: t-103/bots-guild-templates.html §4.
 */

interface Props {
  projectID: string;
  /** `name,description,logo,meta:…` — this project's own header, built from
   *  its schema by the tab, so the dialog and the empty state's «Download
   *  template» cannot disagree about what a file looks like. */
  csvHeader: string;
  metaKeys: CardKey[];
  onClose: () => void;
  /** The real import landed — the tab toasts it and refetches. */
  onDone: (result: GuildTemplateImportResult) => void;
}

export function ImportGuildTemplatesModal({
  projectID,
  csvHeader,
  metaKeys,
  onClose,
  onDone,
}: Props) {
  return (
    <ImportCsvModal<GuildTemplateImportRow, GuildTemplateImportResult>
      title="Import guild templates from CSV"
      nouns={{ one: "template", many: "templates" }}
      dropTitle="Drop a guild templates CSV here"
      fileLabel="Guild templates CSV"
      header={csvHeader}
      modeNote="A row that already became a guild is never touched by an import — that guild is standing, and this table is the only record that the platform stood it up."
      refusedFileName="guild-templates-refused-lines.csv"
      run={(input) => importGuildTemplates(projectID, input)}
      errorOf={guildTemplateError}
      previewHead={
        <>
          {/* The file's own line, as on the Source tab: one dialog, one word
              for the column that points back into the file. */}
          <th className={styles.num} scope="col">
            Line
          </th>
          <th scope="col">Name</th>
          <th scope="col">Description</th>
          <th scope="col">Logo</th>
          <th scope="col">Metadata</th>
        </>
      }
      previewRow={(row, i) => (
        <tr key={`${row.line}-${i}`}>
          <td className={styles.num}>{row.line}</td>
          <td>{row.name}</td>
          <td className={styles.descCell}>{row.description || "—"}</td>
          <td className={styles.mono}>{row.logo || "—"}</td>
          <td className={styles.mono}>
            {cardSummary({}, row.metadata ?? {}, metaKeys) || "—"}
          </td>
        </tr>
      )}
      onClose={onClose}
      onDone={onDone}
    />
  );
}
